import type { ReactNode } from 'react';
import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import type { MediaDimensionValue } from '@/entities/creatives';

interface CreativeDrawerContextState {
  currentTuple: MediaDimensionValue | null;
  openCreativeDrawer: (tuple: MediaDimensionValue) => void;
  closeCreativeDrawer: () => void;
}

const Context = createContext<CreativeDrawerContextState | null>(null);

export function CreativeDrawerContextProvider({ children }: { readonly children: ReactNode }) {
  const [currentTuple, setCurrentTuple] = useState<MediaDimensionValue | null>(null);

  const openCreativeDrawer = useCallback((tuple: MediaDimensionValue) => {
    setCurrentTuple(tuple);
  }, []);

  const closeCreativeDrawer = useCallback(() => setCurrentTuple(null), []);

  const value = useMemo<CreativeDrawerContextState>(
    () => ({ currentTuple, openCreativeDrawer, closeCreativeDrawer }),
    [currentTuple, openCreativeDrawer, closeCreativeDrawer],
  );

  return <Context.Provider value={value}>{children}</Context.Provider>;
}

export function useCreativeDrawerContext() {
  const context = useContext(Context);

  if (!context) {
    throw new Error(
      'useCreativeDrawerContext hook used outside <CreativeDrawerContextProvider />',
    );
  }

  return context;
}

export function useOptionalCreativeDrawerContext() {
  return useContext(Context);
}
